import { ReactNode, useId, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

/**
 * The create flow's form furniture, after the house UI kit
 * (answerrocket/design-system `@answerrocket/ui`): a Card with a mono eyebrow
 * and a display title per section, labels above controls with their hint in
 * plain sight, and a choice between postures drawn as selectable cards rather
 * than a radio list.
 *
 * Why its own module: the kit is Tailwind and this app is Backstage (MUI + BUI),
 * so the components cannot be imported — only their anatomy. Both forms use
 * these (the manifest-driven request page via schemaForm, and the scaffolder
 * wizard via the ChoiceCards field extension), which is what keeps "Deploy a
 * service" and "New environment" looking like one product.
 *
 * Colours are the Catalog's own `--ar-*` tokens (src/theme/design-system.css),
 * so light and dark both follow the rest of the portal.
 */
const useStyles = makeStyles({
  section: {
    border: '1px solid var(--ar-border)',
    borderRadius: 14,
    background: 'var(--ar-surface)',
    boxShadow: '0 6px 24px rgba(17, 24, 39, 0.05)',
    marginBottom: 20,
  },
  // Inside the wizard the page is already a card; a card in a card reads as a
  // box in a box, so sections there are a titled block with a rule above.
  sectionFlat: {
    borderTop: '1px solid var(--ar-border-soft)',
    paddingTop: 18,
    marginBottom: 18,
    '&:first-child': { borderTop: 'none', paddingTop: 0 },
  },
  sectionHead: { padding: '18px 22px 4px' },
  sectionHeadFlat: { paddingBottom: 6 },
  eyebrow: {
    fontFamily: 'var(--ar-font-mono)',
    fontSize: '0.6875rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
  },
  sectionTitle: {
    fontFamily: 'var(--ar-font-display)',
    fontSize: '1.0625rem',
    fontWeight: 650,
    letterSpacing: '-0.01em',
    color: 'var(--ar-fg)',
    marginTop: 3,
  },
  sectionHint: {
    fontSize: '0.875rem',
    lineHeight: 1.55,
    color: 'var(--ar-fg-2)',
    marginTop: 4,
    maxWidth: '62ch',
  },
  sectionBody: {
    padding: '12px 22px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  sectionBodyFlat: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    paddingTop: 8,
  },

  label: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 6,
  },
  labelText: {
    fontSize: '0.875rem',
    fontWeight: 620,
    color: 'var(--ar-fg)',
  },
  labelAside: {
    fontFamily: 'var(--ar-font-mono)',
    fontSize: '0.6875rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
    whiteSpace: 'nowrap',
  },
  hint: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    color: 'var(--ar-fg-2)',
    margin: '-2px 0 10px',
    maxWidth: '68ch',
  },
  more: {
    border: 'none',
    background: 'none',
    padding: 0,
    marginLeft: 4,
    font: 'inherit',
    color: 'var(--ar-accent)',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },

  choices: {
    display: 'grid',
    gap: 10,
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    border: 'none',
    margin: 0,
    padding: 0,
    minWidth: 0,
  },
  choice: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    padding: '12px 40px 12px 14px',
    borderRadius: 12,
    border: '1px solid var(--ar-border-strong)',
    background: 'var(--ar-surface)',
    cursor: 'pointer',
    transition:
      'border-color 160ms cubic-bezier(.22,1,.36,1), background-color 160ms cubic-bezier(.22,1,.36,1)',
    '&:hover': { borderColor: 'var(--ar-accent)' },
    // The native radio stays in the DOM — keyboard, screen readers and tests
    // all use it — but the card is what you see.
    '&:focus-within': {
      outline: '2px solid var(--ar-accent)',
      outlineOffset: 2,
    },
  },
  choiceOn: {
    borderColor: 'var(--ar-accent)',
    background: 'var(--ar-accent-soft)',
    boxShadow: 'inset 0 0 0 1px var(--ar-accent)',
  },
  choiceInput: {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
    margin: 0,
  },
  choiceTitle: {
    fontSize: '0.9375rem',
    fontWeight: 640,
    color: 'var(--ar-fg)',
  },
  choiceHint: {
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    color: 'var(--ar-fg-2)',
  },
  choiceCheck: {
    position: 'absolute',
    top: 11,
    right: 11,
    fontSize: 20,
    color: 'var(--ar-accent)',
  },
  choiceRing: {
    position: 'absolute',
    top: 13,
    right: 13,
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '1.5px solid var(--ar-border-strong)',
  },
});

/** First sentence of a description, so a hint stays one line until asked. */
function splitHint(text: string): [string, string] {
  const match = text.match(/^(.+?[.!?])(\s+)(.+)$/s);
  if (!match || match[1].length < 24) return [text, ''];
  return [match[1], match[3]];
}

/**
 * A field's hint, in plain sight. It replaces the `?` tooltips: the hint is
 * what tells somebody which answer is theirs, and a tooltip is a hint people
 * have to know to go looking for. Long descriptions (Control writes some for
 * agents as much as for people) show their first sentence and a "More".
 */
export function FieldHint(props: { text?: string; id?: string }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  if (!props.text) return null;
  const [first, rest] = splitHint(props.text);
  return (
    <p className={classes.hint} id={props.id}>
      {open || !rest ? props.text : first}
      {rest && (
        <button
          type="button"
          className={classes.more}
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
        >
          {open ? 'Less' : 'More'}
        </button>
      )}
    </p>
  );
}

/** Label row: the question on the left, "Optional" (or similar) on the right. */
export function FieldLabel(props: {
  htmlFor?: string;
  id?: string;
  children: ReactNode;
  aside?: string;
}) {
  const classes = useStyles();
  return (
    <div className={classes.label}>
      <label
        htmlFor={props.htmlFor}
        id={props.id}
        className={classes.labelText}
      >
        {props.children}
      </label>
      {props.aside && <span className={classes.labelAside}>{props.aside}</span>}
    </div>
  );
}

export interface Choice<T extends string | number = string> {
  value: T;
  title: string;
  hint?: string;
}

/**
 * One answer out of a few, each with a sentence on when it is the right one.
 *
 * For choices between postures — which cloud, whose data, where to start —
 * where the explanation IS the decision. A radio list puts that explanation in
 * small print under a 12px dot; a card makes the whole answer the target.
 *
 * Nothing is selected unless `value` says so: a preselected answer is one
 * nobody chose (the cloud field used to arrive preselected as GCP).
 */
export function ChoiceCards<T extends string | number>(props: {
  name: string;
  label?: string;
  description?: string;
  choices: Choice<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  aside?: string;
}) {
  const classes = useStyles();
  const labelId = useId();
  const hintId = useId();
  return (
    <div>
      {props.label && (
        <FieldLabel id={labelId} aside={props.aside}>
          {props.label}
        </FieldLabel>
      )}
      <FieldHint text={props.description} id={hintId} />
      <div
        role="radiogroup"
        aria-labelledby={props.label ? labelId : undefined}
        aria-describedby={props.description ? hintId : undefined}
        className={classes.choices}
      >
        {props.choices.map(choice => {
          const on = props.value === choice.value;
          return (
            <label
              key={String(choice.value)}
              className={`${classes.choice} ${on ? classes.choiceOn : ''}`}
            >
              <input
                type="radio"
                className={classes.choiceInput}
                name={props.name}
                value={String(choice.value)}
                checked={on}
                onChange={() => props.onChange(choice.value)}
              />
              <span className={classes.choiceTitle}>{choice.title}</span>
              {choice.hint && (
                <span className={classes.choiceHint}>{choice.hint}</span>
              )}
              {on ? (
                <CheckCircleIcon className={classes.choiceCheck} aria-hidden />
              ) : (
                <span className={classes.choiceRing} aria-hidden />
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A section of a form: eyebrow ("Step 2"), title, the hint Control published
 * for the group, then its fields. `flat` inside the scaffolder wizard, whose
 * step is already a card.
 */
export function FormSection(props: {
  eyebrow?: string;
  title: string;
  hint?: string;
  flat?: boolean;
  children: ReactNode;
}) {
  const classes = useStyles();
  return (
    <section className={props.flat ? classes.sectionFlat : classes.section}>
      <div
        className={props.flat ? classes.sectionHeadFlat : classes.sectionHead}
      >
        {props.eyebrow && (
          <div className={classes.eyebrow}>{props.eyebrow}</div>
        )}
        <h3 className={classes.sectionTitle}>{props.title}</h3>
        {props.hint && <p className={classes.sectionHint}>{props.hint}</p>}
      </div>
      <div
        className={props.flat ? classes.sectionBodyFlat : classes.sectionBody}
      >
        {props.children}
      </div>
    </section>
  );
}
